import React, { useState } from 'react';
import { API_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Loader2, Send, Building, FileText, Handshake, HelpCircle, Users } from 'lucide-react';



const SUBJECTS = [
  { value: 'publicite', label: 'Demande de publicité (bannière)', icon: FileText },
  { value: 'annonce', label: 'Publication d\'annonce', icon: Building },
  { value: 'proposition_bien', label: 'Proposition de bien à vendre/louer', icon: MapPin },
  { value: 'information', label: 'Demande d\'information', icon: HelpCircle },
  { value: 'devis', label: 'Demande de devis', icon: FileText },
  { value: 'partenariat', label: 'Partenariat', icon: Handshake },
  { value: 'autre', label: 'Autre', icon: Users }
];

const PAYS = ['Sénégal', 'France', 'Autre'];

export default function Contact() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type_personne: 'particulier',
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    raison_sociale: '',
    identifiant_legal: '',
    pays: 'Sénégal',
    sujet: '',
    sujet_autre: '',
    message: '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    whatsapp_numero: '',
    hasWhatsapp: false,
    consentement: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consentement) {
      toast.error('Veuillez accepter d\'être recontacté');
      return;
    }
    
    if (formData.message.length < 30) {
      toast.error('Le message doit contenir au moins 30 caractères');
      return;
    }

    if (formData.sujet === 'autre' && !formData.sujet_autre.trim()) {
      toast.error('Veuillez préciser le sujet');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type_personne: formData.type_personne,
          nom: formData.type_personne === 'particulier' ? formData.nom : null,
          prenom: formData.type_personne === 'particulier' ? formData.prenom : null,
          raison_sociale: formData.type_personne === 'entreprise' ? formData.raison_sociale : null,
          identifiant_legal: formData.type_personne === 'entreprise' ? formData.identifiant_legal : null,
          pays: formData.pays,
          sujet: formData.sujet,
          sujet_autre: formData.sujet === 'autre' ? formData.sujet_autre : null,
          message: formData.message,
          email: formData.email,
          telephone: formData.telephone,
          whatsapp_numero: formData.hasWhatsapp ? formData.whatsapp_numero : null,
          pieces_jointes: []
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success('Votre demande a été envoyée avec succès');
      setFormData({
        ...formData,
        sujet: '',
        sujet_autre: '',
        message: '',
        consentement: false
      });
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="contact-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold font-serif mb-4">Contactez-nous</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Une question, une demande de publicité, une proposition de bien ? 
            Remplissez le formulaire ci-dessous et notre équipe vous répondra dans les plus brefs délais.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Téléphone</h3>
                    <p className="text-sm text-muted-foreground">+221 77 178 73 79</p>
                    <p className="text-sm text-muted-foreground">+33 623 23 73 18</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Email</h3>
                    <p className="text-sm text-muted-foreground">contact@jamm-immo.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Adresse</h3>
                    <p className="text-sm text-muted-foreground">Dakar, Sénégal</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Formulaire de contact</CardTitle>
              <CardDescription>Tous les champs marqués * sont obligatoires</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Type de personne */}
                <div className="space-y-3">
                  <Label>Vous êtes *</Label>
                  <RadioGroup
                    value={formData.type_personne}
                    onValueChange={(v) => setFormData({ ...formData, type_personne: v })}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="particulier" id="particulier" data-testid="type-particulier" />
                      <Label htmlFor="particulier">Particulier</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="entreprise" id="entreprise" data-testid="type-entreprise" />
                      <Label htmlFor="entreprise">Entreprise</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Identity fields */}
                {formData.type_personne === 'particulier' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="prenom">Prénom *</Label>
                      <Input
                        id="prenom"
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        required
                        data-testid="contact-prenom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom">Nom *</Label>
                      <Input
                        id="nom"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        required
                        data-testid="contact-nom"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="raison_sociale">Raison sociale *</Label>
                      <Input
                        id="raison_sociale"
                        value={formData.raison_sociale}
                        onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                        required
                        data-testid="contact-raison-sociale"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="identifiant_legal">NINEA / SIRET / RCCM</Label>
                      <Input
                        id="identifiant_legal"
                        value={formData.identifiant_legal}
                        onChange={(e) => setFormData({ ...formData, identifiant_legal: e.target.value })}
                        data-testid="contact-identifiant"
                      />
                    </div>
                  </div>
                )}

                {/* Pays */}
                <div className="space-y-2">
                  <Label>Pays *</Label>
                  <Select value={formData.pays} onValueChange={(v) => setFormData({ ...formData, pays: v })}>
                    <SelectTrigger data-testid="contact-pays">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYS.map(pays => (
                        <SelectItem key={pays} value={pays}>{pays}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sujet */}
                <div className="space-y-2">
                  <Label>Sujet de votre demande *</Label>
                  <Select value={formData.sujet} onValueChange={(v) => setFormData({ ...formData, sujet: v })}>
                    <SelectTrigger data-testid="contact-sujet">
                      <SelectValue placeholder="Sélectionnez un sujet" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECTS.map(subject => (
                        <SelectItem key={subject.value} value={subject.value}>
                          {subject.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.sujet === 'autre' && (
                  <div className="space-y-2">
                    <Label htmlFor="sujet_autre">Précisez le sujet *</Label>
                    <Input
                      id="sujet_autre"
                      value={formData.sujet_autre}
                      onChange={(e) => setFormData({ ...formData, sujet_autre: e.target.value })}
                      required
                      data-testid="contact-sujet-autre"
                    />
                  </div>
                )}

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">Votre message * (min. 30 caractères)</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    required
                    minLength={30}
                    data-testid="contact-message"
                  />
                  <p className="text-xs text-muted-foreground">{formData.message.length}/30 caractères minimum</p>
                </div>

                {/* Contact */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telephone">Téléphone *</Label>
                    <Input
                      id="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      placeholder="+221 77 000 00 00"
                      required
                      data-testid="contact-telephone"
                    />
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="hasWhatsapp"
                      checked={formData.hasWhatsapp}
                      onCheckedChange={(v) => setFormData({ ...formData, hasWhatsapp: v })}
                      data-testid="contact-has-whatsapp"
                    />
                    <Label htmlFor="hasWhatsapp">Je souhaite être contacté par WhatsApp</Label>
                  </div>
                  {formData.hasWhatsapp && (
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">Numéro WhatsApp</Label>
                      <Input
                        id="whatsapp"
                        type="tel"
                        value={formData.whatsapp_numero}
                        onChange={(e) => setFormData({ ...formData, whatsapp_numero: e.target.value })}
                        placeholder="+221 77 000 00 00"
                        data-testid="contact-whatsapp"
                      />
                    </div>
                  )}
                </div>

                {/* Consentement */}
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="consentement"
                    checked={formData.consentement}
                    onCheckedChange={(v) => setFormData({ ...formData, consentement: v })}
                    data-testid="contact-consentement"
                  />
                  <Label htmlFor="consentement" className="text-sm">
                    J'accepte d'être recontacté par JAMM IMMO concernant ma demande *
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-pill" 
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer ma demande
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
